import { useLocale } from '~/shared/libs/gatsby-plugin-i18n';
import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';

import { useFrontmatter } from '~/shared/contexts/frontmatter';

import { CarouselCustomItem, CarouselMode } from './types';

export type CarouselItems = ReturnType<typeof useCarouselItems>;
export default function useCarouselItems(mode: CarouselMode, customItems?: CarouselCustomItem[]) {
  const data = useStaticQuery(graphql`
    query getCarouselData {
      allPortraitItem: allStrapiGamesiteCarousel(filter: { type: { eq: "portrait" } }, sort: { strapiId: DESC }) {
        nodes {
          game
          environment
          locale
          components {
            title
            description
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 1080
                    height: 1920
                    breakpoints: [360, 640, 1200]
                    tracedSVGOptions: { optTolerance: 2, turdSize: 700 }
                  )
                }
              }
            }
          }
        }
      }
      allLandscapeItem: allStrapiGamesiteCarousel(filter: { type: { eq: "landscape" } }, sort: { strapiId: DESC }) {
        nodes {
          game
          environment
          locale
          components {
            title
            description
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 1920
                    height: 1080
                    breakpoints: [360, 640, 1200]
                    tracedSVGOptions: { optTolerance: 2, turdSize: 700 }
                  )
                }
              }
            }
          }
        }
      }
    }
  `);

  const gameId = useFrontmatter().gameId;
  const locale = useLocale();

  const carouselItems = useMemo(() => {
    const targetNodes = (mode === 'portrait' ? data.allPortraitItem.nodes : data.allLandscapeItem.nodes).find(
      (node: any) =>
        node.environment === (process.env.GATSBY_ENVIRONMENT ?? 'preview') &&
        node.game === gameId &&
        node.locale.toLowerCase() === locale.toLowerCase(),
    );

    if (!targetNodes) {
      return [];
    }

    return targetNodes.components.map((component: any, index: number) => {
      const customItem = customItems?.[index];
      if (!customItem) {
        return component;
      }

      return {
        ...component,
        ...customItem,
        image: component.image,
      };
    });
  }, [mode, data.allPortraitItem.nodes, data.allLandscapeItem.nodes, locale, gameId, customItems]);

  return carouselItems;
}
